import type { CampaignScheduleSourceRecord } from '~/hooks/apis/offers/useGetSalesforceCampaignSchedules';

import { splitSalesforceMultiSelect } from '~/utils/splitSalesforceMultiSelect';

import type { CampaignScheduleParentRow, CampaignScheduleParentType, CampaignSchedulePreview } from './types';

const DYNAMIC_CAMPAIGN_TYPE = {
  OFFLINE_PERIOD: 'Offline Period',
  LE_NEW_OFFER: 'LE – New Offer',
  LE_RE_OPEN: 'LE - Re-Open',
  LUX_PLUS_EXCLUSIVE: 'LuxPlus + Exclusive',
} as const;

const LIST_VISIBILITY_TYPES: Array<string> = [DYNAMIC_CAMPAIGN_TYPE.LE_NEW_OFFER, DYNAMIC_CAMPAIGN_TYPE.LE_RE_OPEN];

const AVAILABILITY_ROW_ID = '__availability__';

// A "- LuxPlus" modifier can be layered onto any base campaign type (e.g. "DYNAMIC - Always On"),
// not just the standalone "LuxPlus + Exclusive" type.
function isLuxPlusCampaignType(campaignType: string): boolean {
  return campaignType === DYNAMIC_CAMPAIGN_TYPE.LUX_PLUS_EXCLUSIVE || campaignType.includes('- LuxPlus');
}

// Maps Salesforce Brand__c values to svc-offer's schedule `brand` slug. Only confirmed matches are included.
export const BRAND_MAP: Record<string, string> = {
  LuxuryEscapes: 'luxuryescapes',
  'Scoopon AU': 'scoopontravel',
  Cudo: 'cudotravel',
  Deals: 'dealstravel',
  'TreatMe (NZ)': 'treatmetravel',
  'Business Traveller': 'lebusinesstraveller',
};

// Maps Salesforce Market__c values to lib-regions 2-letter codes. Countries with no LE distribution region
// (e.g. Switzerland, Cuba, Fiji) are intentionally left unmapped.
export const MARKET_REGION_MAP: Record<string, string> = {
  Australia: 'AU',
  'New Zealand': 'NZ',
  India: 'IN',
  'United States': 'US',
  'Hong Kong': 'HK',
  'United Kingdom': 'GB',
  Singapore: 'SG',
  'South Africa': 'ZA',
  Thailand: 'TH',
  Indonesia: 'ID',
  China: 'CN',
  Taiwan: 'TW',
  Vietnam: 'VN',
  Japan: 'JP',
  Canada: 'CA',
  France: 'FR',
  Germany: 'DE',
  Ireland: 'IE',
  Israel: 'IL',
  Italy: 'IT',
  Korea: 'KR',
  Macau: 'MO',
  Malaysia: 'MY',
  Philippines: 'PH',
  Qatar: 'QA',
  Russia: 'RU',
  'Saudi Arabia': 'SA',
  Spain: 'ES',
  'United Arab Emirates': 'AE',
  'The Netherlands': 'NL',
};

// Earliest start_online__c across every record for the offer — the shared start for both the online_purchase
// row (below) and the fully-derived availability row (see deriveAvailabilityRow).
function earliestStartOnline(schedules: Array<CampaignScheduleSourceRecord>): string | null {
  const starts = schedules.map((schedule) => schedule.start_online__c).filter((value): value is string => !!value);

  return starts.length === 0 ? null : starts.reduce((earliest, start) => (start < earliest ? start : earliest));
}

function unionValues(
  schedules: Array<CampaignScheduleSourceRecord>,
  getValue: (schedule: CampaignScheduleSourceRecord) => string | null | undefined,
): Array<string> {
  return Array.from(new Set(schedules.flatMap((schedule) => splitSalesforceMultiSelect(getValue(schedule)))));
}

export interface DeriveCampaignSchedulePreviewOptions {
  bookByEnd?: string | null;
  excludedBrands?: Array<string>;
}

// Each Campaign_Schedule__c record becomes its own row in `rows` — never merged across records — except
// online_purchase's `start`, which is shared with the derived availability row (see deriveAvailabilityRow).
export function deriveCampaignSchedulePreview(
  campaignSchedules: Array<CampaignScheduleSourceRecord>,
  { bookByEnd, excludedBrands = [] }: DeriveCampaignSchedulePreviewOptions,
): CampaignSchedulePreview {
  const warnings: Array<string> = [];
  const excludedBrandSet = new Set(excludedBrands);
  const rows: Array<CampaignScheduleParentRow> = [];

  if (!bookByEnd) {
    warnings.push('Opportunity.Book_By__c is not set — availability schedules cannot be derived');
  }

  const sharedStart = earliestStartOnline(campaignSchedules);
  if (campaignSchedules.length > 0 && !sharedStart) {
    warnings.push(
      'No Salesforce campaign schedule has a start_online__c set — availability and online_purchase schedules cannot be derived',
    );
  }

  const offlinePeriodRecords = campaignSchedules.filter(
    (schedule) => schedule.dynamic_campaign_type__c === DYNAMIC_CAMPAIGN_TYPE.OFFLINE_PERIOD,
  );
  if (offlinePeriodRecords.length === 0 && campaignSchedules.length > 0) {
    warnings.push(
      'No "Offline Period" Salesforce campaign schedule found — cannot derive an Available Offline (online_purchase) end date',
    );
  } else if (offlinePeriodRecords.length > 1) {
    warnings.push(
      `Multiple "Offline Period" Salesforce campaign schedules found (${offlinePeriodRecords.map((schedule) => schedule.id).join(', ')}) — cannot determine a single Available Offline end date`,
    );
  }
  // Only take the online_purchase start from a single, unambiguous Offline Period record.
  const singleOfflinePeriodId = offlinePeriodRecords.length === 1 ? offlinePeriodRecords[0].id : null;

  campaignSchedules.forEach((schedule) => {
    const campaignType = schedule.dynamic_campaign_type__c;

    function pushRow(
      type: CampaignScheduleParentType,
      start: string | null,
      end: string | null,
      brands: Array<string>,
    ) {
      if (!start || !end) {
        const missing = [!start && 'start date', !end && 'end date'].filter(Boolean).join(' and ');
        warnings.push(
          `Salesforce campaign schedule ${schedule.id} (${campaignType ?? 'unknown type'}) is missing its ${missing} — it will not appear below until Salesforce has both dates`,
        );
        return;
      }

      rows.push({
        id: schedule.id,
        type,
        start,
        end,
        startOffline: schedule.start_offline__c ?? null,
        endOffline: schedule.end_offline__c ?? null,
        startOnline: schedule.start_online__c ?? null,
        endOnline: schedule.end_online__c ?? null,
        brands,
        markets: splitSalesforceMultiSelect(schedule.market__c),
        campaignTypes: campaignType ? [campaignType] : [],
      });
    }

    if (campaignType && isLuxPlusCampaignType(campaignType)) {
      // Checked first: a "- LuxPlus" modifier can be layered onto any base campaign type (e.g. a
      // dynamic Always-On type), so it must take priority over the Offline Period / List Visibility
      // checks below rather than only matching the standalone "LuxPlus + Exclusive" type.
      pushRow(
        'lux_plus',
        schedule.start_online__c,
        schedule.end_online__c,
        splitSalesforceMultiSelect(schedule.brand__c),
      );
    } else if (campaignType === DYNAMIC_CAMPAIGN_TYPE.OFFLINE_PERIOD) {
      // Skipped (warnings already pushed above) when there's no shared start or an ambiguous Offline Period.
      if (sharedStart && schedule.id === singleOfflinePeriodId) {
        pushRow('online_purchase', sharedStart, schedule.end_offline__c, splitSalesforceMultiSelect(schedule.brand__c));
      }
    } else if (campaignType && LIST_VISIBILITY_TYPES.includes(campaignType)) {
      pushRow(
        'list_visibility',
        schedule.start_online__c,
        schedule.end_online__c,
        splitSalesforceMultiSelect(schedule.brand__c).filter((brand) => !excludedBrandSet.has(brand)),
      );
    } else {
      warnings.push(
        `Salesforce campaign schedule ${schedule.id} has an unrecognized dynamic_campaign_type__c "${campaignType ?? 'none'}" — it will not appear below`,
      );
    }
  });

  const derivedSchedules = deriveAvailabilityRow(campaignSchedules, sharedStart, bookByEnd);

  return { rows, warnings, derivedSchedules };
}

// availability has no Salesforce record of its own, so unlike online_purchase it's never shown in
// `rows` — only in `derivedSchedules`, to satisfy the import dependency chain.
function deriveAvailabilityRow(
  campaignSchedules: Array<CampaignScheduleSourceRecord>,
  sharedStart: string | null,
  bookByEnd: string | null | undefined,
): Array<CampaignScheduleParentRow> {
  if (!sharedStart || !bookByEnd) {
    return [];
  }

  return [
    {
      id: AVAILABILITY_ROW_ID,
      type: 'availability',
      start: sharedStart,
      end: bookByEnd,
      startOffline: null,
      endOffline: null,
      startOnline: sharedStart,
      endOnline: null,
      brands: unionValues(campaignSchedules, (schedule) => schedule.brand__c),
      markets: unionValues(campaignSchedules, (schedule) => schedule.market__c),
      campaignTypes: [],
    },
  ];
}
